import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  TrendingUp, TrendingDown, DollarSign, Wallet, CreditCard, 
  Building2, Users, AlertTriangle, ArrowUpRight, ArrowDownRight,
  Calendar, RefreshCw, PieChart as PieChartIcon, BarChart3,
  Banknote, Receipt, Clock, Target, Loader2, Video, Send, Phone
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';

import { BACKEND_URL } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
const API = BACKEND_URL;

// Alias for compatibility
const formatFullCurrency = formatCurrency;

const CEODashboard = ({ user }) => {
  const [period, setPeriod] = useState('this_month');
  const [data, setData] = useState(null);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Telegram Video Report states
  const [showVideoDialog, setShowVideoDialog] = useState(false);
  const [chatIds, setChatIds] = useState('');
  const [sendingReport, setSendingReport] = useState(false);
  const [generatingVideo, setGeneratingVideo] = useState(false);
  const [telegramConfigured, setTelegramConfigured] = useState(null);
  const [botInfo, setBotInfo] = useState(null);
  const [sendingCashFlowReport, setSendingCashFlowReport] = useState(false);

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

  // Send Cash Flow PDF Report via Telegram
  const handleSendCashFlowReport = async () => {
    setSendingCashFlowReport(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/cash-flow/send-telegram?period=this_month`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(`Cash Flow reports sent to ${response.data.success_count}/${response.data.total_recipients} recipients!`);
      } else {
        toast.error('Failed to send Cash Flow reports');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to send Cash Flow report';
      toast.error(errorMsg);
    } finally {
      setSendingCashFlowReport(false);
    }
  };

  const checkTelegramStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/ceo-report/telegram-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTelegramConfigured(response.data.configured);
      if (response.data.bot_info) {
        setBotInfo(response.data.bot_info);
      }
    } catch (error) {
      console.error('Failed to check Telegram status:', error);
      setTelegramConfigured(false);
    }
  };

  const fetchDashboard = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast.error('Not authenticated. Please login again.');
        setLoading(false);
        return;
      }
      
      console.log('[CEO Dashboard] Fetching data for period:', period);
      console.log('[CEO Dashboard] API URL:', `${API}/api/ceo/dashboard`);
      
      // Fetch dashboard data
      const dashRes = await axios.get(`${API}/api/ceo/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { period }
      });
      
      console.log('[CEO Dashboard] Dashboard data received:', dashRes.status);
      
      // Fetch trends data (optional - don't fail if this errors)
      let trendsData = null;
      try {
        const trendsRes = await axios.get(`${API}/api/ceo/trends`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { days: 30 }
        });
        trendsData = trendsRes.data;
        console.log('[CEO Dashboard] Trends data received:', trendsRes.status);
      } catch (trendError) {
        console.warn('[CEO Dashboard] Failed to fetch trends:', trendError.message);
      }
      
      setData(dashRes.data);
      setTrends(trendsData);
    } catch (error) {
      console.error('[CEO Dashboard] Error:', error);
      console.error('[CEO Dashboard] Response:', error.response?.data);
      console.error('[CEO Dashboard] Status:', error.response?.status);
      
      let errorMsg = 'Failed to load dashboard data';
      if (error.response?.status === 404) {
        errorMsg = 'CEO Dashboard API not found. Please ensure the latest version is deployed.';
      } else if (error.response?.status === 403) {
        errorMsg = 'Access denied. You may not have permission to view CEO Dashboard.';
      } else if (error.response?.status === 500) {
        errorMsg = 'Server error. Please check server logs.';
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      }
      
      toast.error(errorMsg);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    checkTelegramStatus();
  }, [period]);

  const handleGenerateVideo = async () => {
    setGeneratingVideo(true);
    try {
      const token = localStorage.getItem('token');
      
      // Start video generation (background task)
      const response = await axios.post(`${API}/api/ceo-report/generate`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { period: 'yesterday' }
      });
      
      if (response.data.success && response.data.job_id) {
        toast.info('Video generation started... This may take 30-60 seconds.');
        
        // Poll for status
        const jobId = response.data.job_id;
        let attempts = 0;
        const maxAttempts = 60; // 2 minutes max
        
        const pollStatus = async () => {
          attempts++;
          try {
            const statusResponse = await axios.get(
              `${API}/api/ceo-report/generate/status/${jobId}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            const status = statusResponse.data;
            
            if (status.status === 'completed') {
              setGeneratingVideo(false);
              if (status.video_url) {
                toast.success('Video generated! Opening in new tab...');
                window.open(status.video_url, '_blank');
              } else {
                toast.success('Video generated successfully!');
              }
              return;
            } else if (status.status === 'failed') {
              setGeneratingVideo(false);
              toast.error(status.error || 'Video generation failed');
              return;
            } else if (status.status === 'processing') {
              // Show progress
              if (status.progress) {
                toast.info(status.progress, { id: 'video-progress' });
              }
              
              if (attempts < maxAttempts) {
                setTimeout(pollStatus, 2000); // Poll every 2 seconds
              } else {
                setGeneratingVideo(false);
                toast.error('Video generation timed out. Please try again.');
              }
            }
          } catch (pollError) {
            if (attempts < maxAttempts) {
              setTimeout(pollStatus, 2000);
            } else {
              setGeneratingVideo(false);
              toast.error('Failed to check video status');
            }
          }
        };
        
        // Start polling after 2 seconds
        setTimeout(pollStatus, 2000);
        
      } else if (response.data.video_url) {
        // Immediate response (shouldn't happen with new API but handle it)
        toast.success('Video generated! Opening in new tab...');
        window.open(response.data.video_url, '_blank');
        setGeneratingVideo(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate video');
      setGeneratingVideo(false);
    }
  };

  const handleSendReport = async () => {
    if (!chatIds.trim()) {
      toast.error('Please enter at least one Telegram Chat ID');
      return;
    }
    
    const ids = chatIds.split(',').map(n => n.trim()).filter(n => n);
    if (ids.length === 0) {
      toast.error('Please enter valid Chat IDs');
      return;
    }
    
    setSendingReport(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/ceo-report/send`, {
        chat_ids: ids,
        period: 'yesterday'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        const result = response.data.telegram_result;
        toast.success(`Report sent to ${result.success_count}/${result.total} recipients!`);
        setShowVideoDialog(false);
        setChatIds('');
      } else {
        toast.error(response.data.error || 'Failed to send report');
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail || error.response?.data?.error || 'Failed to send report';
      toast.error(errorDetail);
    } finally {
      setSendingReport(false);
    }
  };

  const getPeriodLabel = () => {
    switch (period) {
      case 'yesterday': return 'Yesterday';
      case 'this_week': return 'This Week (Last 7 Days)';
      case 'this_month': return 'This Month';
      case 'last_month': return 'Last Month';
      default: return 'This Month';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!data) return null;

  const { summary, revenue_breakdown, expense_breakdown, cash_position, pending, projections, comparison, alerts, daily_data } = data;

  // Prepare chart data
  const revenueChartData = [
    { name: 'Cash Sales', value: revenue_breakdown.cash_sales, color: '#10b981' },
    { name: 'Online Payments', value: revenue_breakdown.online_payments, color: '#3b82f6' },
    { name: 'Delivery Apps', value: revenue_breakdown.delivery_apps, color: '#f59e0b' }
  ].filter(d => d.value > 0);

  const expenseChartData = [
    { name: 'Vendor Payments', value: expense_breakdown.vendor_payments, color: '#ef4444' },
    { name: 'Salary', value: expense_breakdown.salary_payments, color: '#8b5cf6' },
    { name: 'Utilities', value: expense_breakdown.utility_payments, color: '#06b6d4' },
    { name: 'Other', value: expense_breakdown.other_expenses, color: '#6b7280' }
  ].filter(d => d.value > 0);

  return (
    <div className="space-y-6 p-6 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen" data-testid="ceo-dashboard">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3">
            <BarChart3 className="w-8 h-8 text-blue-600" />
            CEO Dashboard
          </h1>
          <p className="text-slate-500 mt-1">Executive overview of business performance</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[200px] bg-white">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="yesterday">Yesterday</SelectItem>
              <SelectItem value="this_week">This Week (7 Days)</SelectItem>
              <SelectItem value="this_month">This Month</SelectItem>
              <SelectItem value="last_month">Last Month</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Send Cash Flow Report Button */}
          <Button 
            className="bg-green-600 hover:bg-green-700 text-white"
            onClick={handleSendCashFlowReport}
            disabled={sendingCashFlowReport}
            data-testid="send-cashflow-telegram-btn"
          >
            {sendingCashFlowReport ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            Send Cash Flow Report
          </Button>
          
          {/* Telegram Video Report Button (Legacy) */}
          <Dialog open={showVideoDialog} onOpenChange={setShowVideoDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-blue-300 text-blue-600 hover:bg-blue-50">
                <Video className="w-4 h-4 mr-2" />
                Video Report
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Video className="w-5 h-5 text-blue-500" />
                  CEO Video Report
                </DialogTitle>
                <DialogDescription>
                  Generate a 30-second animated video summary and send via Telegram
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Telegram Status */}
                <div className={`p-3 rounded-lg ${telegramConfigured ? 'bg-blue-50 border border-blue-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                  <div className="flex items-center gap-2">
                    <Send className={`w-4 h-4 ${telegramConfigured ? 'text-blue-600' : 'text-yellow-600'}`} />
                    <span className={`text-sm font-medium ${telegramConfigured ? 'text-blue-700' : 'text-yellow-700'}`}>
                      {telegramConfigured ? `Telegram Bot Connected${botInfo ? ` (@${botInfo.username})` : ''}` : 'Telegram Bot Not Configured'}
                    </span>
                  </div>
                  {!telegramConfigured && (
                    <p className="text-xs text-yellow-600 mt-1">
                      Add TELEGRAM_BOT_TOKEN in backend/.env (get from @BotFather on Telegram)
                    </p>
                  )}
                </div>
                
                {/* Generate Video Only */}
                <div>
                  <Label className="text-sm font-medium">Quick Actions</Label>
                  <Button
                    onClick={handleGenerateVideo}
                    disabled={generatingVideo}
                    variant="outline"
                    className="w-full mt-2"
                  >
                    {generatingVideo ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating Video...
                      </>
                    ) : (
                      <>
                        <Video className="w-4 h-4 mr-2" />
                        Generate & Download Video
                      </>
                    )}
                  </Button>
                </div>
                
                {/* Send via Telegram */}
                {telegramConfigured && (
                  <div className="border-t pt-4">
                    <Label htmlFor="chat-ids" className="text-sm font-medium">
                      Send to Telegram Chat IDs
                    </Label>
                    <Input
                      id="chat-ids"
                      placeholder="e.g., 123456789, -987654321"
                      value={chatIds}
                      onChange={(e) => setChatIds(e.target.value)}
                      className="mt-2"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Enter Chat IDs separated by commas. Get your ID by messaging the bot and checking /getUpdates
                    </p>
                  </div>
                )}
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowVideoDialog(false)}>
                  Cancel
                </Button>
                {telegramConfigured && (
                  <Button
                    onClick={handleSendReport}
                    disabled={sendingReport || !chatIds.trim()}
                    className="bg-blue-500 hover:bg-blue-600"
                  >
                    {sendingReport ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send to Telegram
                      </>
                    )}
                  </Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          <Button variant="outline" onClick={() => fetchDashboard(true)} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Alerts Section */}
      {alerts && alerts.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {alerts.map((alert, idx) => (
            <Card key={idx} className={`border-l-4 ${
              alert.type === 'danger' ? 'border-l-red-500 bg-red-50' :
              alert.type === 'warning' ? 'border-l-yellow-500 bg-yellow-50' :
              'border-l-blue-500 bg-blue-50'
            }`}>
              <CardContent className="py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className={`w-5 h-5 ${
                    alert.type === 'danger' ? 'text-red-500' :
                    alert.type === 'warning' ? 'text-yellow-500' :
                    'text-blue-500'
                  }`} />
                  <span className="font-medium">{alert.message}</span>
                </div>
                <Badge variant="outline">{alert.action}</Badge>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {/* Gross Sales */}
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="pt-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-blue-100 text-sm font-medium">Gross Sales</p>
                <p className="text-2xl font-bold mt-1">{formatCurrency(summary.gross_sales)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-blue-200" />
            </div>
            {comparison && (
              <div className={`flex items-center mt-3 text-sm ${comparison.sales_change_percent >= 0 ? 'text-green-200' : 'text-red-200'}`}>
                {comparison.sales_change_percent >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                <span>{Math.abs(comparison.sales_change_percent)}% {comparison.period}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Net Sales */}
        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
          <CardContent className="pt-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-emerald-100 text-sm font-medium">Net Sales</p>
                <p className="text-2xl font-bold mt-1">{formatCurrency(summary.net_sales)}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-emerald-200" />
            </div>
            <p className="text-emerald-200 text-xs mt-3">After NC: {formatCurrency(summary.nc_amount)}</p>
          </CardContent>
        </Card>

        {/* Total Expenses */}
        <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
          <CardContent className="pt-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-red-100 text-sm font-medium">Total Expenses</p>
                <p className="text-2xl font-bold mt-1">{formatCurrency(summary.total_expenses)}</p>
              </div>
              <Receipt className="w-8 h-8 text-red-200" />
            </div>
            <p className="text-red-200 text-xs mt-3">Deductions: {formatCurrency(summary.total_deductions)}</p>
          </CardContent>
        </Card>

        {/* Net Profit */}
        <Card className={`bg-gradient-to-br ${summary.net_profit >= 0 ? 'from-green-500 to-green-600' : 'from-orange-500 to-orange-600'} text-white`}>
          <CardContent className="pt-6">
            <div className="flex justify-between items-start">
              <div>
                <p className={`${summary.net_profit >= 0 ? 'text-green-100' : 'text-orange-100'} text-sm font-medium`}>Net Profit</p>
                <p className="text-2xl font-bold mt-1">{formatCurrency(Math.abs(summary.net_profit))}</p>
              </div>
              {summary.net_profit >= 0 ? <TrendingUp className="w-8 h-8 text-green-200" /> : <TrendingDown className="w-8 h-8 text-orange-200" />}
            </div>
            <p className={`${summary.net_profit >= 0 ? 'text-green-200' : 'text-orange-200'} text-xs mt-3`}>
              {summary.net_profit < 0 && '- '}{summary.profit_margin.toFixed(1)}% margin
            </p>
          </CardContent>
        </Card>

        {/* Cash Position */}
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="pt-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-purple-100 text-sm font-medium">Total Liquid</p>
                <p className="text-2xl font-bold mt-1">{formatCurrency(cash_position.total_liquid)}</p>
              </div>
              <Wallet className="w-8 h-8 text-purple-200" />
            </div>
            <p className="text-purple-200 text-xs mt-3">Cash + Bank Balance</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sales Trend Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-500" />
              Sales Trend (Last 30 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {trends && trends.daily_trends.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={trends.daily_trends}>
                  <defs>
                    <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 11 }}
                    tickFormatter={(val) => val.slice(5)}
                  />
                  <YAxis 
                    tick={{ fontSize: 11 }}
                    tickFormatter={(val) => `₹${(val/1000).toFixed(0)}K`}
                  />
                  <Tooltip 
                    formatter={(value) => formatFullCurrency(value)}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="gross_sales" 
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    fill="url(#colorSales)"
                    name="Gross Sales"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="net_sales" 
                    stroke="#10b981" 
                    strokeWidth={2}
                    dot={false}
                    name="Net Sales"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-gray-500">
                No trend data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Revenue Breakdown Pie */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChartIcon className="w-5 h-5 text-green-500" />
              Revenue Breakdown - {getPeriodLabel()}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {revenueChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={revenueChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {revenueChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatFullCurrency(value)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-gray-500">
                No revenue data for this period
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Second Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Expense Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-red-500" />
              Expense Breakdown - {getPeriodLabel()}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {expenseChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={expenseChartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" tickFormatter={(val) => `₹${(val/1000).toFixed(0)}K`} />
                  <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value) => formatFullCurrency(value)} />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {expenseChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-gray-500">
                No expense data for this period
              </div>
            )}
          </CardContent>
        </Card>

        {/* Cash & Bank Position */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-purple-500" />
              Cash & Bank Position
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Cash in Hand */}
            <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Banknote className="w-8 h-8 text-green-600" />
                <div>
                  <p className="text-sm text-green-700">Cash in Hand</p>
                  <p className="text-xl font-bold text-green-800">{formatFullCurrency(cash_position.cash_in_hand)}</p>
                </div>
              </div>
            </div>

            {/* Bank Accounts */}
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(cash_position.bank_balances).map(([account, balance]) => (
                <div key={account} className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-600 font-medium">{account}</p>
                  <p className="text-lg font-bold text-blue-800">{formatCurrency(balance)}</p>
                </div>
              ))}
            </div>

            {/* Pending Liabilities */}
            <div className="border-t pt-4 mt-4">
              <p className="text-sm font-medium text-gray-600 mb-3">Pending Liabilities</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-red-50 rounded-lg">
                  <p className="text-xs text-red-600">Vendor Dues</p>
                  <p className="text-lg font-bold text-red-700">{formatCurrency(pending.vendor_dues)}</p>
                </div>
                <div className="p-3 bg-orange-50 rounded-lg">
                  <p className="text-xs text-orange-600">Pending Salaries</p>
                  <p className="text-lg font-bold text-orange-700">{formatCurrency(pending.pending_salaries)}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Projections Card (only for this_month) */}
      {projections && (
        <Card className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Target className="w-5 h-5" />
              This Month Projections
            </CardTitle>
            <CardDescription className="text-indigo-100">
              Based on {projections.days_elapsed} days data, projecting for full {projections.days_in_month} days
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <p className="text-indigo-200 text-sm">Daily Avg Sales</p>
                <p className="text-2xl font-bold">{formatCurrency(projections.daily_avg_sales)}</p>
              </div>
              <div>
                <p className="text-indigo-200 text-sm">Projected Sales</p>
                <p className="text-2xl font-bold">{formatCurrency(projections.projected_sales)}</p>
              </div>
              <div>
                <p className="text-indigo-200 text-sm">Projected Expenses</p>
                <p className="text-2xl font-bold">{formatCurrency(projections.projected_expenses)}</p>
              </div>
              <div>
                <p className="text-indigo-200 text-sm">Projected Profit</p>
                <p className="text-2xl font-bold">{formatCurrency(projections.projected_profit)}</p>
                <p className="text-indigo-200 text-xs mt-1">{projections.projected_margin}% margin</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Daily Data Table */}
      {daily_data && daily_data.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-slate-500" />
              Daily Breakdown - {getPeriodLabel()}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left p-3 font-semibold">Date</th>
                    <th className="text-right p-3 font-semibold">Gross Sales</th>
                    <th className="text-right p-3 font-semibold">Net Sales</th>
                    <th className="text-right p-3 font-semibold">Deductions</th>
                    <th className="text-right p-3 font-semibold">Closing Cash</th>
                  </tr>
                </thead>
                <tbody>
                  {daily_data.map((day, idx) => (
                    <tr key={day.date} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 font-medium">{day.date}</td>
                      <td className="p-3 text-right text-blue-600">{formatFullCurrency(day.gross_sales)}</td>
                      <td className="p-3 text-right text-green-600">{formatFullCurrency(day.net_sales)}</td>
                      <td className="p-3 text-right text-red-600">{formatFullCurrency(day.deductions)}</td>
                      <td className="p-3 text-right font-medium">{formatFullCurrency(day.closing_cash)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 bg-slate-100 font-bold">
                    <td className="p-3">TOTAL</td>
                    <td className="p-3 text-right text-blue-700">{formatFullCurrency(summary.gross_sales)}</td>
                    <td className="p-3 text-right text-green-700">{formatFullCurrency(summary.net_sales)}</td>
                    <td className="p-3 text-right text-red-700">{formatFullCurrency(summary.total_deductions)}</td>
                    <td className="p-3 text-right">-</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Footer */}
      <div className="text-center text-sm text-slate-500 py-4">
        Data as of {new Date(data.meta.generated_at).toLocaleString()} • {data.meta.day_closings_count} day closings analyzed
      </div>
    </div>
  );
};

export default CEODashboard;
